import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import {
    createAppClient,
    fetchAppChecklist,
    normalizeApiErrorDetails,
    type AdminRegistryApp,
    type AppChecklist,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { Button, Callout, ConfirmButton, Modal, useToast } from '@/components/ui';
import { formatCatalogAge } from './catalogAge';
import styles from './AppOnboardingModal.module.css';

interface AppOnboardingModalProps {
    /** The app whose onboarding/OIDC-client state is being viewed; `null` closes the modal. */
    app: AdminRegistryApp | null;
    canManage: boolean;
    onClose: () => void;
}

interface RevealedClient {
    client_id: string;
    client_secret: string;
    rotated: boolean;
    warning: string;
    next_steps: AppChecklist;
}

const KEYCLOAK_PROVISIONING_FAILED_CODE = 6006;

/** `EXT_PROVISIONING_FAILED` details, as returned by `POST /admin/apps/{code}/client`. */
function keycloakErrorDetail(error: unknown): string | null {
    if (!(error instanceof OrgAdminApiError)) return null;
    if (error.code !== KEYCLOAK_PROVISIONING_FAILED_CODE) return null;
    return normalizeApiErrorDetails(error.details).keycloak?.[0] ?? null;
}

function ChecklistStep({ index, label, done }: { index: number; label: string; done: boolean }) {
    return (
        <li className={done ? `${styles.step} is-done` : styles.step}>
            <span className={styles.stepIndex} aria-hidden="true">
                {done ? '✓' : index}
            </span>
            <span>{label}</span>
        </li>
    );
}

function ChecklistSteps({ checklist }: { checklist: AppChecklist }) {
    const { t } = useTranslation();
    return (
        <ol className={styles.checklist}>
            <ChecklistStep
                index={1}
                label={t('console.apps.onboardingModal.checklistCatalogSynced')}
                done={checklist.catalog_synced}
            />
            <ChecklistStep
                index={2}
                label={t('console.apps.onboardingModal.checklistRoleLinked')}
                done={checklist.role_link_count > 0}
            />
            <ChecklistStep
                index={3}
                label={t('console.apps.onboardingModal.checklistApproved')}
                done={checklist.enabled}
            />
        </ol>
    );
}

/**
 * EAA-1817: row-menu modal for a single app's onboarding state (frame 48) —
 * the 3-step checklist (`fetchAppChecklist`) plus the one-time OIDC client
 * create/rotate reveal (`createAppClient`).
 *
 * `client_secret` is PLAINTEXT and returned in THIS ONE RESPONSE ONLY — no
 * read endpoint (registry, checklist, or otherwise) can ever surface it
 * again. It is therefore kept in local component state ONLY (`revealed`
 * below), never written into a TanStack Query cache, and is dropped for good
 * on close/unmount — there is no retry path that fetches it a second time.
 */
export function AppOnboardingModal({ app, canManage, onClose }: AppOnboardingModalProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();

    const [revealed, setRevealed] = useState<RevealedClient | null>(null);

    const checklistQuery = useQuery({
        queryKey: ['app-checklist', app?.code, accessToken],
        queryFn: () =>
            fetchAppChecklist(config.apiBase, accessToken as string, app?.code as string),
        enabled: app !== null,
        retry: false,
    });

    const clientMutation = useMutation({
        mutationFn: () =>
            createAppClient(config.apiBase, accessToken as string, app?.code as string),
        onSuccess: (data) => {
            setRevealed(data);
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            void queryClient.invalidateQueries({ queryKey: ['app-checklist', app?.code] });
        },
        onError: () => {
            toast.error(t('console.apps.onboardingModal.createClientErrorToast'));
        },
    });

    function handleClose() {
        if (clientMutation.isPending) return;
        setRevealed(null);
        onClose();
    }

    async function copy(label: string, value: string) {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(t('console.apps.onboardingModal.copiedToast', { label }));
        } catch {
            toast.error(t('console.apps.onboardingModal.copyErrorToast', { label }));
        }
    }

    if (!app) return null;

    const keycloakDetail = keycloakErrorDetail(clientMutation.error);
    const hasLoginClient = app.app_type !== 'api';
    const hasMachineClient = app.app_type !== 'web';

    return (
        <Modal
            open={app !== null}
            onCancel={handleClose}
            title={
                revealed
                    ? t('console.apps.onboardingModal.grantedTitle', { name: app.name })
                    : t('console.apps.onboardingModal.title', { name: app.name })
            }
            // EAA-1846: design frame 46 ("chuỗi bí mật một lần") measures this
            // modal at 520px, not 560 — the same width the "Thêm ứng dụng" form
            // used before it moved from a modal (`CreateAppModal`) to the routed
            // page at /console/apps/new (`AppsCreatePage`).
            width={520}
            footer={
                // EAA-1817: raw <button> → kit Button (compliance sweep).
                <Button onClick={handleClose}>
                    {revealed ? t('console.apps.onboardingModal.doneButton') : t('common.close')}
                </Button>
            }
        >
            {revealed ? (
                <>
                    <p>{t('console.apps.onboardingModal.secretOnceWarning')}</p>
                    {/* EAA-1846: frame 46 draws two independent labelled chips
                        (label above a code+copy row) rather than the shared
                        `.portal-admin-secret` warning-bordered card those other
                        screens use — kept as its own page-scoped class so this
                        doesn't change the six other consumers of that class.
                        The "copy" trigger is antd's own `type="link"` variant
                        (no new component), matching the frame's plain teal
                        text rather than a bordered button. */}
                    <div className={styles.secret} role="alert">
                        <div className={styles.secretField}>
                            <span className={styles.secretLabel}>
                                {t('console.apps.onboardingModal.clientIdFieldLabel')}
                            </span>
                            <span className={styles.secretBox}>
                                <code className={styles.secretValue}>{revealed.client_id}</code>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() =>
                                        copy(
                                            t('console.apps.onboardingModal.clientIdLabel'),
                                            revealed.client_id
                                        )
                                    }
                                >
                                    {t('console.apps.onboardingModal.copyButton')}
                                </Button>
                            </span>
                        </div>
                        <div className={styles.secretField}>
                            <span className={styles.secretLabel}>
                                {t('console.apps.onboardingModal.secretFieldLabel')}
                            </span>
                            <span className={styles.secretBox}>
                                <code className={styles.secretValue}>{revealed.client_secret}</code>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() =>
                                        copy(
                                            t('console.apps.onboardingModal.clientSecretLabel'),
                                            revealed.client_secret
                                        )
                                    }
                                >
                                    {t('console.apps.onboardingModal.copyButton')}
                                </Button>
                            </span>
                        </div>
                    </div>
                    <Callout
                        tone="danger"
                        label={t('console.apps.onboardingModal.closeLosesSecretLabel')}
                        longLabel
                    >
                        {revealed.warning ||
                            t('console.apps.onboardingModal.secretLossFallback')}
                    </Callout>
                    {revealed.rotated && (
                        <Callout tone="warning">
                            {t('console.apps.onboardingModal.rotatedWarning')}
                        </Callout>
                    )}
                    {/* EAA-1846: frame 46's "Ba bước còn lại" checklist sits
                        inside its own muted card, not a bare heading + list. */}
                    <div className={styles.checklistCard}>
                        <span className={styles.checklistCardLabel}>
                            {t('console.apps.onboardingModal.remainingStepsTitle')}
                        </span>
                        <ChecklistSteps checklist={revealed.next_steps} />
                    </div>
                    <p className="portal-admin-hint">
                        <Link to={`/console/role-map?application=${encodeURIComponent(app.code)}`}>
                            {t('console.apps.onboardingModal.goToRoleMapLink')}
                        </Link>
                    </p>
                </>
            ) : (
                <>
                    {/* EAA-1817: reuses `.portal-console-config` — the same dt/dd grid
                        `/console/config` already established for read-only key/value
                        metadata, no new layout needed for this block. */}
                    {/* EAA-1709: the login client (web/hybrid) is public + PKCE and
                        has no secret; the secret this modal issues belongs to the
                        app backend's machine client `<code>-api` (api/hybrid). */}
                    <dl className="portal-console-config">
                        {hasLoginClient && (
                            <>
                                <dt>{t('console.apps.onboardingModal.loginClientIdLabel')}</dt>
                                <dd>
                                    {app.kc_client_id ? (
                                        <code>{app.kc_client_id}</code>
                                    ) : (
                                        t('console.apps.onboardingModal.notGrantedYet')
                                    )}
                                </dd>
                            </>
                        )}
                        {hasMachineClient && (
                            <>
                                <dt>{t('console.apps.onboardingModal.apiClientIdLabel')}</dt>
                                <dd>
                                    {app.kc_api_client_id ? (
                                        <code>{app.kc_api_client_id}</code>
                                    ) : (
                                        t('console.apps.onboardingModal.notGrantedYet')
                                    )}
                                </dd>
                            </>
                        )}
                        <dt>{t('console.apps.onboardingModal.clientCreatedAtLabel')}</dt>
                        <dd>{formatCatalogAge(app.kc_client_created_at)}</dd>
                        {hasMachineClient && (
                            <>
                                <dt>{t('console.apps.onboardingModal.secretRotatedAtLabel')}</dt>
                                <dd>{formatCatalogAge(app.kc_api_secret_rotated_at)}</dd>
                            </>
                        )}
                    </dl>
                    {!hasMachineClient && (
                        <p className="portal-admin-hint">
                            {t('console.apps.onboardingModal.publicClientNote')}
                        </p>
                    )}

                    {checklistQuery.isLoading && (
                        <p aria-busy="true">{t('console.apps.onboardingModal.checklistLoading')}</p>
                    )}
                    {checklistQuery.isError && (
                        <p className="portal-admin-error">
                            {t('console.apps.onboardingModal.checklistErrorMessage')}
                        </p>
                    )}
                    {checklistQuery.data && <ChecklistSteps checklist={checklistQuery.data} />}

                    {clientMutation.isError && (
                        <p className="portal-admin-error">
                            {keycloakDetail ??
                                t('console.apps.onboardingModal.createClientErrorMessage')}
                        </p>
                    )}

                    {canManage &&
                        hasMachineClient &&
                        (app.kc_api_client_id ? (
                            <ConfirmButton
                                type="primary"
                                danger
                                label={
                                    clientMutation.isPending
                                        ? t('console.apps.onboardingModal.rotatingLabel')
                                        : t('console.apps.onboardingModal.rotateSecretButton')
                                }
                                disabled={clientMutation.isPending}
                                confirmTitle={t('console.apps.onboardingModal.rotateConfirmTitle')}
                                confirmDescription={t(
                                    'console.apps.onboardingModal.rotateConfirmDescription'
                                )}
                                onConfirm={() => clientMutation.mutate()}
                            />
                        ) : (
                            <Button
                                type="primary"
                                onClick={() => clientMutation.mutate()}
                                disabled={clientMutation.isPending}
                            >
                                {clientMutation.isPending
                                    ? t('console.apps.onboardingModal.creatingLabel')
                                    : t('console.apps.onboardingModal.createClientButton')}
                            </Button>
                        ))}
                </>
            )}
        </Modal>
    );
}
